"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { apiService } from "../../services/apiService"

const Dashboard = () => {
  const [stats, setStats] = useState({
    total_members: 0,
    total_mentors: 0,
    total_prayer_requests: 0,
    total_testimonies: 0,
    pending_light_spot_requests: 0
  })
  const [recentActivities, setRecentActivities] = useState([])
  const [upcomingEvents, setUpcomingEvents] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setIsLoading(true)
      setError("")

      // Get dashboard stats
      const dashboardStats = await apiService.getDashboardStats()
      setStats(dashboardStats)

      // Get recent activities (combining prayer requests, testimonies, and light spot requests)
      const [prayerRequests, testimonies, lightSpotRequests] = await Promise.all([
        apiService.getPrayerRequests(),
        apiService.getTestimonies(),
        apiService.getLightSpotRequests()
      ])

      const activities = [
        ...prayerRequests.map(request => ({
          id: request.id,
          activity: `New prayer request from ${request.name}`,
          time: new Date(request.created_at).toLocaleString()
        })),
        ...testimonies.map(testimony => ({
          id: testimony.id,
          activity: `New testimony shared by ${testimony.name}`,
          time: new Date(testimony.created_at).toLocaleString()
        })),
        ...lightSpotRequests.map(request => ({
          id: request.id,
          activity: `New Light Spot request from ${request.name}`,
          time: new Date(request.created_at).toLocaleString()
        }))
      ].sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 5)

      setRecentActivities(activities)

      // Get upcoming events
      const events = await apiService.getEvents()
      const upcoming = events
        .filter(event => new Date(event.date) > new Date())
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 3)
        .map(event => ({
          id: event.id,
          title: event.title,
          date: new Date(event.date).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          }),
          time: event.time || 'All day'
        }))

      setUpcomingEvents(upcoming)
    } catch (error) {
      console.error('Error loading dashboard data:', error)
      setError(error.message || 'Failed to load dashboard data')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return <div className="loading">Loading dashboard data...</div>
  }

  if (error) {
    return <div className="error-message">{error}</div>
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
      </div>
      <div className="dashboard">
        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-icon">
              <i className="material-icons">people</i>
            </div>
            <div className="stat-info">
              <h3>{stats.total_members}</h3>
              <p>Total Members</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="material-icons">volunteer_activism</i>
            </div>
            <div className="stat-info">
              <h3>{stats.total_prayer_requests}</h3>
              <p>Prayer Requests</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="material-icons">record_voice_over</i>
            </div>
            <div className="stat-info">
              <h3>{stats.total_testimonies}</h3>
              <p>Testimonies</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="material-icons">supervisor_account</i>
            </div>
            <div className="stat-info">
              <h3>{stats.total_mentors}</h3>
              <p>Mentors</p>
            </div>
          </div>
        </div>

        <div className="dashboard-row">
          <div className="dashboard-col">
            <h2 className="section-title">Recent Activities</h2>
            <div className="card">
              <ul className="activity-list">
                {recentActivities.map((activity) => (
                  <li key={activity.id} className="activity-item">
                    <div className="activity-content">
                      <p>{activity.activity}</p>
                      <span className="activity-time">{activity.time}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="dashboard-col">
            <h2 className="section-title">Upcoming Events</h2>
            <div className="card">
              <ul className="event-list">
                {upcomingEvents.map((event) => (
                  <li key={event.id} className="event-item">
                    <div className="event-date">
                      <span className="event-day">{event.date.split(",")[0].split(" ")[1]}</span>
                      <span className="event-month">{event.date.split(",")[0].split(" ")[0]}</span>
                    </div>
                    <div className="event-content">
                      <h3 className="event-title">{event.title}</h3>
                      <p className="event-time">
                        {event.date} at {event.time}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="dashboard-col">
          <h2 className="section-title">Quick Actions</h2>
          <div className="quick-actions">
            <Link to="/admin/devotions" className="quick-action-card">
              <i className="material-icons">video_library</i>
              <span>Upload Devotion</span>
            </Link>
            <Link to="/admin/announcements" className="quick-action-card">
              <i className="material-icons">campaign</i>
              <span>Post Announcement</span>
            </Link>
            <Link to="/admin/events" className="quick-action-card">
              <i className="material-icons">event</i>
              <span>Add Event</span>
            </Link>
            <Link to="/admin/quotes" className="quick-action-card">
              <i className="material-icons">format_quote</i>
              <span>Add Quote</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
